import AddressComplete from "components/address-complete/address-complete.component";
import Input from "components/input/input.component";
import Select from "components/select/select.component";
import {
  DisclaimerContainer,
  FormPartContainer,
  RequiredText,
  SectionHeading,
  InputRow,
  TwoColumnsInput,
  BottomButtonsContainer,
  NextStepButton,
} from "global";
import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  updateDonationInfo,
  updateDonorInfo,
  updateFormErrors,
  updateSecuritiesInfo,
} from "redux/form/form.reducer";
import {
  selectFormLanguage,
  selectDonorInfo,
  selectFormErrors,
  selectDonationInfo,
  selectSecuritiesInfo,
} from "redux/form/form.selector";
import {
  updateCurrentForm,
  updatePopup,
  updatePresValue,
} from "redux/helpers/helpers.reducer";
import datalayerAPI from "utils/datalayerAPI";
import {
  addressRegex,
  countriesCollection,
  emailRegex,
  getTitleSelectValues,
  managementOptions,
  nameRegex,
  phoneNumberRegex,
  popupMessages,
  postalCodeRegex,
  provincesCollection,
} from "utils/form-constants";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { CardBlock } from "./gos-donor-info-step.styles";
import { ManagementTypes, ValidationErrors } from "utils/interfaces";
import {
  scrollToTheError,
  scrollToTheTop,
  validate,
} from "utils/helper-functions";
import { ValidationError } from "yup";
import { selectCurrentForm, selectPopup } from "redux/helpers/helpers.selector";
import { gosDonorInfoSchema } from "utils/validation-schemas";
import ErrorMessage from "components/error-message/error-message.component";

const GosDonorInfoStep: FC = () => {
  // URL parameters
  const [searchParams] = useSearchParams();
  const presValueURL = searchParams.get("pres") || searchParams.get("s_pres");

  const donationInfo = useAppSelector(selectDonationInfo);
  const securitiesInfo = useAppSelector(selectSecuritiesInfo);
  const formErrors = useAppSelector(selectFormErrors);
  const donorInfo = useAppSelector(selectDonorInfo);
  const formLanguage = useAppSelector(selectFormLanguage);
  const popup = useAppSelector(selectPopup);
  const currentForm = useAppSelector(selectCurrentForm);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const lastTrackedValuesRef = useRef<Record<string, string>>({
    email: donorInfo.email,
    companyName: donorInfo.companyName,
    firstName: donorInfo.firstName,
    lastName: donorInfo.lastName,
    addressLine2: donorInfo.addressLine2,
    city: donorInfo.city,
    provinceState: donorInfo.provinceState,
    postal: donorInfo.postal,
    phoneNumber: donorInfo.phoneNumber,
  });

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (lastTrackedValuesRef.current[name] !== value) {
      lastTrackedValuesRef.current[name] = value;
      datalayerAPI.handleFieldValueChange(
        name === "email" ? "emailAddress" : name,
      );
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    ignoreTracking?: boolean,
  ) => {
    const { name, value } = e.currentTarget;
    const isCurrentlyFocused = document.activeElement === e.currentTarget;

    switch (name) {
      case "firstName":
      case "lastName":
        if (nameRegex.test(value))
          dispatch(updateDonorInfo({ ...donorInfo, [name]: value }));
        break;
      case "email":
        if (!emailRegex.test(value)) return;
        if (!presValueURL) dispatch(updatePresValue("default"));
        dispatch(updateDonorInfo({ ...donorInfo, email: value }));
        break;
      case "postal":
        if (!postalCodeRegex.test(value)) return;
        dispatch(updateDonationInfo({ ...donationInfo, utcOffset: undefined }));
        dispatch(updateDonorInfo({ ...donorInfo, postal: value }));
        break;
      case "city":
      case "provinceState":
        if (!addressRegex.test(value)) return;
        dispatch(updateDonationInfo({ ...donationInfo, utcOffset: undefined }));
        dispatch(updateDonorInfo({ ...donorInfo, [name]: value }));
        break;
      case "addressLine2":
        if (!addressRegex.test(value)) return;
        dispatch(
          updateDonorInfo({
            ...donorInfo,
            addressLine2: value.replace(/=/g, ""),
          }),
        );
        break;
      case "phoneNumber":
        if (!phoneNumberRegex.test(value)) return;
        dispatch(
          updateDonorInfo({
            ...donorInfo,
            phoneNumber: value,
          }),
        );
        break;
      default:
        break;
    }

    // If field is NOT focused, this was browser autofill - fire event immediately
    if (
      !ignoreTracking &&
      !isCurrentlyFocused &&
      lastTrackedValuesRef.current[name] !== value
    ) {
      lastTrackedValuesRef.current[name] = value;
      datalayerAPI.handleFieldValueChange(
        name === "email" ? "emailAddress" : name,
      );
    }
  };

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.currentTarget;
    datalayerAPI.handleFieldValueChange(name, value);

    switch (name) {
      case "title":
        dispatch(updateDonorInfo({ ...donorInfo, title: value }));
        break;
      case "appealCodeOverride":
        dispatch(
          updateDonationInfo({ ...donationInfo, appealCodeOverride: value }),
        );
        break;
      case "provinceState":
        dispatch(updateDonationInfo({ ...donationInfo, utcOffset: undefined }));
        dispatch(updateDonorInfo({ ...donorInfo, provinceState: value }));
        break;
      default:
        break;
    }
  };

  const handleManagementOptionClick = (optionValue: ManagementTypes) => {
    dispatch(
      updateSecuritiesInfo({
        ...securitiesInfo,
        securitiesManagementType: optionValue,
      }),
    );
  };

  const handleNextStep = async () => {
    try {
      // Validate
      await validate(
        {
          ...donorInfo,
          securitiesManagementType: securitiesInfo.securitiesManagementType,
        },
        gosDonorInfoSchema,
      );
      dispatch(updateCurrentForm(1));
      dispatch(updateFormErrors({}));

      // Scroll to the top
      scrollToTheTop();
    } catch (error: unknown) {
      const errors: ValidationErrors = {};

      (error as ValidationError).inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });

      scrollToTheError(Object.keys(errors)[0]);

      // Display popup
      dispatch(updateFormErrors(errors));
      dispatch(
        updatePopup({
          ...popup,
          message: popupMessages.errorsFixed,
          isError: true,
          isActive: true,
          isLoading: false,
        }),
      );
    }
  };

  return (
    <FormPartContainer currentForm={currentForm} formID={0}>
      <DisclaimerContainer style={{ marginBottom: 32 }}>
        {t("GOS Disclaimer")}
      </DisclaimerContainer>

      <SectionHeading>{t("Donor information")}</SectionHeading>
      <RequiredText>* {t("Required info")}</RequiredText>

      <div>
        <InputRow>
          <Select
            name="title"
            id="title"
            onChange={handleSelectChange}
            label="Title"
            value={donorInfo.title}
            optionsData={getTitleSelectValues(formLanguage)}
            isError={formErrors.title}
            isRequired
            withTranslation
            withValueTranslation
          />

          <Input
            label="First Name"
            type="text"
            name="firstName"
            id="firstName"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            value={donorInfo.firstName}
            isError={formErrors.firstName}
            maxLength={50}
            isRequired
          />
          <Input
            label="Last Name"
            type="text"
            name="lastName"
            id="lastName"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            value={donorInfo.lastName}
            isError={formErrors.lastName}
            maxLength={100}
            isRequired
          />
        </InputRow>

        <AddressComplete
          countryToLookup={donorInfo.country}
          addressType="donor"
        />
        <Input
          label="City"
          type="text"
          name="city"
          id="city"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          value={donorInfo.city}
          isError={formErrors.city}
          maxLength={50}
          isRequired
        />

        <TwoColumnsInput isMobileFullWidth>
          <Select
            label="State / Province"
            name="provinceState"
            id="provinceState"
            onChange={handleSelectChange}
            value={donorInfo.provinceState}
            isError={formErrors.provinceState}
            optionsData={provincesCollection}
            withTranslation
            isRequired
          />
          <Input
            label="Postal"
            type="text"
            name="postal"
            id="postal"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            value={donorInfo.postal}
            isError={formErrors.postal}
            maxLength={7}
            isRequired
          />
        </TwoColumnsInput>
        <Select
          label="Country"
          name="country"
          id="country"
          onChange={handleSelectChange}
          value={donorInfo.country}
          isError={formErrors.country}
          optionsData={countriesCollection}
          invalidValueFallbackToBlank
          disabled
          isRequired
        />
        <Input
          label="Address 2"
          type="text"
          name="addressLine2"
          id="addressLine2"
          onChange={(e) => handleInputChange(e, true)}
          value={donorInfo.addressLine2}
          maxLength={200}
        />
        <TwoColumnsInput isMobileFullWidth>
          <Input
            label="Phone Number"
            type="phone"
            maxLength={50}
            name="phoneNumber"
            id="phoneNumber"
            onChange={handleInputChange}
            value={donorInfo.phoneNumber}
            isError={formErrors.phoneNumber}
            isRequired
          />
          <Input
            label="Email Address"
            type="email"
            name="email"
            id="email"
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            value={donorInfo.email}
            isError={formErrors.email}
            maxLength={150}
            isRequired
          />
        </TwoColumnsInput>

        <SectionHeading>
          {t("How are your investments managed?")}
        </SectionHeading>

        <div style={{ display: "flex", gap: 38 }}>
          {managementOptions.map((option) => (
            <CardBlock
              key={option.value}
              onClick={() => handleManagementOptionClick(option.value)}
              isActive={
                securitiesInfo.securitiesManagementType === option.value
              }
              tabIndex={0}
            >
              <h5>{t(option.title)}</h5>
              <p>{t(option.subtitle)}</p>
            </CardBlock>
          ))}
        </div>

        {formErrors.securitiesManagementType && (
          <ErrorMessage errorMessage={formErrors.securitiesManagementType} />
        )}
      </div>

      <BottomButtonsContainer>
        <NextStepButton onClick={handleNextStep}>
          {t("Next Step")}
        </NextStepButton>
      </BottomButtonsContainer>
    </FormPartContainer>
  );
};

export default GosDonorInfoStep;
